import {
  type SignatureCaptureOptions,
  type SignatureCaptureProvider,
  type SignatureDeviceStatus
} from "./signature-provider.type";
import {
  SigWebError,
  clearSignature,
  describeSigWebFailure,
  getInkPreviewBlob,
  getPointCount,
  getSigWebVersion,
  getSignatureImageBlob,
  getTabletModel,
  startCapture,
  stopCapture,
  toDeviceFailure
} from "./sigweb.client";

/** Filename the canvas path uses — kept identical so downstream is unchanged. */
export const SIGNATURE_FILE_NAME = "signature.png";

/**
 * Re-encode whatever SigWeb served into a PNG `File`.
 *
 * The service does not document the format of `SigImage/1`, and the vendor's
 * own helper does exactly this — decodes the blob into an `<img>` and re-emits
 * it via `canvas.toDataURL("image/png")`. Everything downstream (S3 upload,
 * Anvil PDF fill, the react-pdf footer) is handed a file named "signature.png"
 * and trusts that, so guessing the type off the wire is not an option.
 *
 * Whatever alpha SigWeb produced is preserved — the canvas starts transparent
 * and nothing is painted behind the decoded image.
 */
async function blobToPngFile(blob: Blob): Promise<File> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () =>
        reject(
          new SigWebError(
            "The signature pad returned an image the browser could not read."
          )
        );
      element.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new SigWebError("Could not render the captured signature.");
    }
    context.drawImage(image, 0, 0);

    const pngBlob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    if (!pngBlob) {
      throw new SigWebError("Could not render the captured signature.");
    }
    return new File([pngBlob], SIGNATURE_FILE_NAME, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Topaz signature pad, reached through the locally installed SigWeb service.
 *
 * Emits the exact same artifact as the canvas desk: a transparent PNG `File`
 * named "signature.png", so `onCapture` consumers are byte-for-byte unaffected.
 */
export const topazSignatureProvider: SignatureCaptureProvider = {
  method: "topaz",

  async isAvailable(): Promise<SignatureDeviceStatus> {
    try {
      const version = await getSigWebVersion();
      // Model is a label for support and RUM, so it is read only once the
      // service has answered and it never decides availability — an older
      // firmware that has no such property still reports a usable pad.
      const model = await getTabletModel();
      return { available: true, version: version || undefined, model };
    } catch (error) {
      return {
        available: false,
        error: describeSigWebFailure(error),
        failure: toDeviceFailure(error)
      };
    }
  },

  async start({ width, height }: SignatureCaptureOptions): Promise<void> {
    await startCapture(width, height);
  },

  async pointCount(): Promise<number> {
    return getPointCount();
  },

  async preview(): Promise<Blob | null> {
    try {
      const blob = await getInkPreviewBlob();
      return blob.size > 0 ? blob : null;
    } catch {
      // The mirror is decoration on top of a working pad; never let it fail
      // the session it decorates.
      return null;
    }
  },

  async capture(): Promise<File> {
    if ((await getPointCount()) === 0) {
      throw new SigWebError(
        "No signature detected on the pad. Please sign, then confirm."
      );
    }

    const blob = await getSignatureImageBlob();
    if (blob.size === 0) {
      throw new SigWebError(
        "The signature pad returned an empty image. Please try signing again."
      );
    }

    return blobToPngFile(blob);
  },

  async clear(): Promise<void> {
    await clearSignature();
  },

  async stop(): Promise<void> {
    await stopCapture();
  }
};
